import React from 'react';
import { formatToMTCO2e, formatNumberWithCommas } from '../../lib/emission-categories';

interface KPIHeaderCardsProps {
  totalEmissions: number;
  offsets?: number;
  companyName?: string;
  selectedYear?: number;
}

export function KPIHeaderCards({ 
  totalEmissions, 
  offsets = 0, 
  companyName = 'Your Company',
  selectedYear = new Date().getFullYear()
}: KPIHeaderCardsProps) {
  const actualImpact = totalEmissions;
  const netImpact = actualImpact - offsets;

  return (
    <div className="bg-gradient-to-r from-green-600 to-green-800 text-white py-4 px-4 rounded-lg mb-4 relative overflow-hidden">
      {/* Decorative background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1 right-1 w-8 h-8 bg-white rounded-full"></div>
        <div className="absolute top-2 right-4 w-4 h-4 bg-white rounded-full"></div>
        <div className="absolute top-3 right-8 w-2 h-2 bg-white rounded-full"></div>
      </div>

      {/* Header */}
      <div className="relative z-10">
        <h1 className="text-sm md:text-base font-bold mb-1">
          Sustainability KPI Dashboard
        </h1>
        <p className="text-green-100 text-xs mb-3">
          {selectedYear}
        </p>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {/* Actual Impact Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-md p-2 border border-white/20">
            <div className="flex items-center mb-1">
              <div className="w-4 h-4 bg-white/20 rounded-sm flex items-center justify-center mr-1">
                <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5.5 16a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 16h-8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xs font-medium text-green-100 uppercase tracking-wide">
                  {selectedYear} ACTUAL IMPACT
                </h3>
              </div>
            </div>
            <div className="text-sm font-bold text-white mb-1">
              {formatNumberWithCommas(parseFloat(formatToMTCO2e(actualImpact)))}
            </div>
            <div className="text-green-100 text-xs font-medium">
              MTCO2e
            </div>
          </div>

          {/* Offsets Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-md p-2 border border-white/20">
            <div className="flex items-center mb-1">
              <div className="w-4 h-4 bg-white/20 rounded-sm flex items-center justify-center mr-1">
                <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm8 0a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V8zm0 4a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-xs font-medium text-green-100 uppercase tracking-wide">
                  {selectedYear} OFFSETS
                </h3>
              </div>
            </div>
            <div className="text-sm font-bold text-white mb-1">
              {formatNumberWithCommas(parseFloat(formatToMTCO2e(offsets)))}
            </div>
            <div className="text-green-100 text-xs font-medium">
              MTCO2e
            </div>
            {offsets === 0 && (
              <div className="text-green-200 text-xs mt-1 italic">
                Coming soon
              </div>
            )}
          </div>

          {/* Net Impact Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-md p-2 border border-white/20">
            <div className="flex items-center mb-1">
              <div className="w-4 h-4 bg-white/20 rounded-sm flex items-center justify-center mr-1">
                <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-xs font-medium text-green-100 uppercase tracking-wide">
                  {selectedYear} NET IMPACT
                </h3>
              </div>
            </div>
            <div className="text-sm font-bold text-white mb-1">
              {formatNumberWithCommas(parseFloat(formatToMTCO2e(netImpact)))}
            </div>
            <div className="text-green-100 text-xs font-medium">
              MTCO2e
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
